import React, { useState } from 'react';
import { X, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BeatPurchaseModal = ({ isOpen, onClose, beat, licenseType }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    artistName: '',
    customerEmail: '',
    customerPhone: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen || !beat) return null;

  const prices = {
    lease: { amount: beat.price || 40, label: 'Lease License', desc: '1 Song, Non-exclusive' },
    unlimited: { amount: beat.unlimited || 80, label: 'Unlimited License', desc: 'Unlimited Songs, No Expiration' },
    exclusive: { amount: beat.exclusive, label: 'Exclusive Rights', desc: 'Full Rights + Stems' }
  };

  const selectedLicense = prices[licenseType];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const response = await axios.post(
        `/api/beats/purchase`,
        {
          beat_id: beat.id,
          license_type: licenseType,
          customer_name: formData.customerName,
          artist_name: formData.artistName,
          customer_email: formData.customerEmail,
          customer_phone: formData.customerPhone
        }
      );

      toast.success('Purchase successful! Check your email for confirmation.');
      
      // Show additional message for exclusive
      if (licenseType === 'exclusive') {
        setTimeout(() => {
          toast.info('Exclusive purchase pending approval. You will be notified within 24-48 hours.');
        }, 2000);
      }

      onClose();
      setFormData({
        customerName: '',
        artistName: '',
        customerEmail: '',
        customerPhone: ''
      });
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Purchase failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-purple-500/30">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Purchase Beat</h2>
            <p className="text-gray-400 text-sm">{beat.title}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-purple-900/30 hover:bg-purple-900/50 flex items-center justify-center transition-colors"
          >
            <X className="text-white" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* License Info */}
          <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-lg">{selectedLicense.label}</h3>
                <p className="text-gray-400 text-sm">{selectedLicense.desc}</p>
              </div>
              <div className="text-3xl font-bold text-green-400">${selectedLicense.amount}</div>
            </div>
            
            {licenseType === 'exclusive' && (
              <div className="mt-4 pt-4 border-t border-purple-500/20">
                <p className="text-yellow-400 text-sm font-semibold">
                  ⚠️ Exclusive purchases require approval (24-48 hours)
                </p>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Full Legal Name *
              </label>
              <input
                type="text"
                required
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                placeholder="John Doe"
                className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Artist / Stage Name *
              </label>
              <input
                type="text"
                required
                value={formData.artistName}
                onChange={(e) => setFormData({ ...formData, artistName: e.target.value })}
                placeholder="Your Stage Name"
                className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                required
                value={formData.customerEmail}
                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                placeholder="your@email.com"
                className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                required
                value={formData.customerPhone}
                onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </div>

            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
              <p className="text-xs text-gray-300 mb-2">
                <span className="font-semibold text-white">Next Steps:</span>
              </p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• Contract will be sent to your email</li>
                <li>• Producer will sign and forward completed contract</li>
                {licenseType === 'exclusive' ? (
                  <li>• Beat files sent after approval (24-48 hours)</li>
                ) : (
                  <li>• Beat will be delivered within 24-48 hours</li>
                )}
              </ul>
            </div>

            <button
              type="submit"
              disabled={isProcessing}
              className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <ShoppingCart size={20} />
                  <span>Purchase for ${selectedLicense.amount}</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BeatPurchaseModal;
